import {
  type LinksFunction,
  type LoaderFunction,
  type MetaFunction,
} from "@remix-run/node";
import { json } from "@remix-run/node";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import getUser from "./kernel/auth/getUser.server";
import Heading from "./ui/Heading";
import LinkButton from "./ui/LinkButton";
import ROUTES from "~/kernel/routing/ROUTES";
import title from "./kernel/meta/title";
import isServer from "./kernel/server/isServer";
import Link from "./ui/Link";
import CONTACT_EMAIL from "./constants/contact/CONTACT_EMAIL";
import log from "./kernel/logging/log";
import toBoolean from "./kernel/form/toBoolean";
import { useTranslation } from "react-i18next";
import { useChangeLanguage } from "remix-i18next/react";
import detectLocale from "./kernel/internationalization/detectLocale";

import "./tailwind.css";
import "./styles/reset.css";
import "./styles/scrollbars.css";
import "./styles/checkbox.css";
import "./styles/fontawesome.css";
import { useReportError } from "./routes/report-error/route";
import { useMarkHydration } from "./kernel/testing/useMarkHydration";
import { SystemMessage } from "./systemMessage/components/SystemMessage";
import useOptionalUser from "./kernel/auth/useOptionalUser";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import LogRocket from "logrocket";
import { representationSession } from "./kernel/auth/representationSession";
import type { RootLoaderData } from "./kernel/routing/RootLoaderData";
import AnalyticsProvider from "./analytics/components/AnalyticsProvider";
import { withTelemetry } from "./kernel/telemetry/withTelemetry";
import { useSessionReplay } from "./kernel/telemetry/useSessionReplay";

export const links: LinksFunction = () => {
  return [
    { rel: "manifest", href: "/manifest.json" },
    {
      rel: "icon",
      href: "/favicon-16x16.png",
      type: "image/png",
      sizes: "16x16",
    },
    {
      rel: "icon",
      href: "/favicon-32x32.png",
      type: "image/png",
      sizes: "32x32",
    },
    {
      rel: "icon",
      href: "/favicon-64x64.png",
      type: "image/png",
      sizes: "64x64",
    },
    {
      rel: "apple-touch-icon",
      href: "/apple-touch-icon-120x120.png",
      sizes: "120x120",
    },
    {
      rel: "apple-touch-icon-precomposed",
      href: "/apple-touch-icon-120x120.png",
      sizes: "120x120",
    },
  ];
};

export const meta: MetaFunction = () => [
  { title: title("Bokningsystem för Hundfrisörer") },
];

declare global {
  interface Window {
    ENV: RootLoaderData["ENV"];
  }
}

export const loader: LoaderFunction = async ({ request }) => {
  const locale = await detectLocale(request);
  const representation = await representationSession.get(request);

  return json<RootLoaderData>({
    user: await getUser(request),
    representation,
    useLogRocket: toBoolean(process.env.ENABLE_LOG_ROCKET),
    locale,
    ENV: {
      ENABLE_SENTRY: toBoolean(process.env.ENABLE_SENTRY),
      ENVIRONMENT: process.env.ENVIRONMENT ?? "development",
    },
  });
};

export const ErrorBoundary = () => {
  const error = useRouteError();
  const reportError = useReportError();
  log.error(error);

  if (isServer()) {
    reportError({
      tag: "root",
      error,
    });
  }

  if (isRouteErrorResponse(error)) {
    return (
      <html lang="sv">
        <head>
          <title>Oops!</title>
          <GlobalMeta />
          <Meta />
          <Links />
        </head>
        <body>
          <div className="mx-auto space-y-5 py-10 md:max-w-lg">
            <img
              alt="Dog peeking over edge"
              className="w-full"
              src="/dog-peeking-over-edge.svg"
            />
            <Heading>
              Oops! ({error.status} {error.statusText})
            </Heading>
            <p>
              Felet kan bero på att sidan du försöker nå inte finns längre,
              eller att något gick snett.
            </p>
            <LinkButton to={ROUTES.root.path()}>Ta mig hem</LinkButton>
          </div>
        </body>
      </html>
    );
  }

  return (
    <html lang="sv">
      <head>
        <title>Oh no!</title>
        <GlobalMeta />
        <Meta />
        <Links />
      </head>
      <body>
        <div className="mx-auto space-y-5 py-10 md:max-w-lg">
          <img
            alt="Dog peeking over edge"
            className="w-full"
            src="/dog-peeking-over-edge.svg"
          />
          <Heading>Något gick fel</Heading>
          <p>
            Vi stötte på ett problem som vi inte vet hur vi ska hantera... ännu!
            Vi kollar på felet så fort vi kan. Om du vill hjälpa oss lösa
            problemet ännu snabbare skicka gärna ett meddelande till{" "}
            <Link external to={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </Link>{" "}
          </p>

          <LinkButton to={ROUTES.root.path()}>Ta mig hem</LinkButton>
        </div>
        <Scripts />
      </body>
    </html>
  );
};

function Head() {
  /**
   * This workaround allows cypress to inject its code to the head. Without it
   * react will switch to client side rendering which might introduce race conditions.
   * This is not ideal and can hopefylly be removed in future versions of react.
   *
   * The markup is captured once so later renders never rewrite the head, which
   * lets ClientMeta own the title after hydration.
   *
   * Issue: https://github.com/facebook/react/issues/24430
   * Workaround: https://github.com/facebook/react/issues/24430#issuecomment-1132209213
   **/
  const [hydratedHead] = useState(() => globalThis.document?.head?.innerHTML);

  if (hydratedHead) {
    return <head dangerouslySetInnerHTML={{ __html: hydratedHead }} />;
  }

  return (
    <head>
      <GlobalMeta />
      <Meta />
      <Links />
    </head>
  );
}

// The frozen head never re-renders, so route meta is portaled in after
// hydration to keep the document title in sync with client side navigation.
function ClientMeta() {
  const [head, setHead] = useState<HTMLHeadElement>();

  useEffect(() => {
    document.head.querySelector("title")?.remove();
    setHead(document.head);
  }, []);

  if (!head) {
    return null;
  }

  return createPortal(<Meta />, head);
}

function Root() {
  const { locale } = useLoaderData<RootLoaderData>();
  const { i18n } = useTranslation();

  useChangeLanguage(locale);
  useLogRocket();
  useMarkHydration();
  useSessionReplay();

  return (
    <html lang={locale} className="h-full" dir={i18n.dir()}>
      <Head />
      <body className="min-h-full">
        <ClientMeta />
        <main id="root">
          <AnalyticsProvider>
            <SystemMessage />
            <Outlet />
          </AnalyticsProvider>
        </main>
        <ScrollRestoration />
        <AssignEnvToWindow />
        <Scripts />
      </body>
    </html>
  );
}

export default withTelemetry(Root);

function GlobalMeta() {
  return (
    <>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width,initial-scale=1" />
      <meta name="format-detection" content="telephone=no" />
      <meta
        name="description"
        content="Ett system för att hundfrisörer lättare och snabbare ska kunna boka in sina kunder."
      />
    </>
  );
}

function AssignEnvToWindow() {
  const { ENV } = useLoaderData<RootLoaderData>();

  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `
          window.ENV = ${JSON.stringify(ENV)};
        `,
      }}
    />
  );
}

const LOG_ROCKET_APP_ID = "bjiolz/bokahundtrim";

function useLogRocket() {
  const user = useOptionalUser();
  const userId = user?.id;
  const loaderData = useLoaderData<RootLoaderData>();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!loaderData.useLogRocket) {
      return undefined;
    }

    if (!isInitialized) {
      LogRocket.init(LOG_ROCKET_APP_ID);
      setIsInitialized(true);
    }

    if (userId) {
      LogRocket.identify(userId);
    }
  }, [userId, isInitialized, loaderData.useLogRocket]);
}
